import { MockServiceStore } from "./MockServiceStore";
import { mockPurchaseOrders } from "./purchase-orders.mock";

/**
 * Queue types
 */
export type QueueType = "receiving" | "approval" | "payment" | "rejection";

/**
 * Task status
 */
export type TaskStatus = "pending" | "in_progress" | "completed" | "cancelled";

/**
 * Task priority
 */
export type TaskPriority = "low" | "normal" | "high" | "urgent";

/**
 * Approval Queue Task entity type
 */
export interface ApprovalQueueTask {
  taskId: number;
  queueType: QueueType;
  entityType: "purchase_order" | "invoice" | "payment";
  entityId: number;
  entityNumber?: string; // PO number, invoice number, etc.
  assignedTo?: number;
  assignedToName?: string;
  plantId: number;
  plantCode?: string;
  title: string;
  description?: string;
  priority: TaskPriority;
  status: TaskStatus;
  createdDate: string;
  dueDate?: string;
  completedDate?: string;
  completedBy?: number;
  completedByName?: string;
  action?: "approved" | "rejected" | "received" | "paid" | "flagged";
  actionNotes?: string;
  actionBy?: number;
  actionByName?: string;
  // Resolution fields
  resolution?: "approved" | "rejected" | "received" | "paid" | "flagged";
  notes?: string;
  receivingNumber?: string;
  paymentNumber?: string;
  // Denormalized data for display
  vendorName?: string;
  totalAmount?: number;
  invoiceNumber?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * User names for assignment
 */
const userNames: Record<string, string> = {
  receiving: "Jason",
  approval: "Clay",
  payment: "Allie",
  rejection: "Salesperson",
};

/**
 * Generate tasks from POs based on status
 */
const generateMockTasks = (): ApprovalQueueTask[] => {
  const tasks: ApprovalQueueTask[] = [];
  let taskId = 1;

  mockPurchaseOrders.forEach((po) => {
    const mapPriorityToTask = (poPriority: string): TaskPriority => {
      switch (poPriority) {
        case "urgent":
          return "urgent";
        case "high":
          return "high";
        case "medium":
          return "normal";
        default:
          return "low";
      }
    };

    // Generate tasks based on PO status
    if (po.status === "pending_receiving" || po.status === "submitted") {
      // Receiving queue task
      const dueDate = new Date(po.expectedDeliveryDate || po.orderDate);
      tasks.push({
        taskId: taskId++,
        queueType: "receiving",
        entityType: "purchase_order",
        entityId: po.purchaseOrderId,
        entityNumber: po.poNumber,
        assignedTo: 2, // Jason
        assignedToName: "Jason",
        plantId: po.plantId,
        plantCode: po.plantCode,
        title: `Receive PO ${po.poNumber}`,
        description: `${po.vendorName} - Expected ${po.expectedDeliveryDate}`,
        priority: mapPriorityToTask(po.priority),
        status: "pending",
        createdDate: po.orderDate,
        dueDate: dueDate.toISOString().split("T")[0],
        vendorName: po.vendorName,
        totalAmount: po.totalAmount,
        createdAt: po.createdAt,
        updatedAt: po.updatedAt,
      });
    }

    if (po.status === "pending_approval" || po.status === "received") {
      // Approval queue task
      tasks.push({
        taskId: taskId++,
        queueType: "approval",
        entityType: "purchase_order",
        entityId: po.purchaseOrderId,
        entityNumber: po.poNumber,
        assignedTo: 1, // Clay
        assignedToName: "Clay",
        plantId: po.plantId,
        plantCode: po.plantCode,
        title: `Approve PO ${po.poNumber}`,
        description: `${po.vendorName} - $${po.totalAmount.toLocaleString()}`,
        priority: mapPriorityToTask(po.priority),
        status: "pending",
        createdDate: po.receivedDate || po.orderDate,
        dueDate: undefined,
        vendorName: po.vendorName,
        totalAmount: po.totalAmount,
        createdAt: po.createdAt,
        updatedAt: po.updatedAt,
      });
    }

    if (po.status === "approved" || po.status === "payment_scheduled") {
      // Payment queue task
      const dueDate = new Date();
      dueDate.setDate(dueDate.getDate() + 30); // Net 30
      tasks.push({
        taskId: taskId++,
        queueType: "payment",
        entityType: "purchase_order",
        entityId: po.purchaseOrderId,
        entityNumber: po.poNumber,
        assignedTo: 3, // Allie
        assignedToName: "Allie",
        plantId: po.plantId,
        plantCode: po.plantCode,
        title: `Pay PO ${po.poNumber}`,
        description: `${po.vendorName} - $${po.totalAmount.toLocaleString()}`,
        priority: mapPriorityToTask(po.priority),
        status: po.status === "payment_scheduled" ? "in_progress" : "pending",
        createdDate: po.approvedDate || po.orderDate,
        dueDate: dueDate.toISOString().split("T")[0],
        vendorName: po.vendorName,
        totalAmount: po.totalAmount,
        createdAt: po.createdAt,
        updatedAt: po.updatedAt,
      });
    }

    if (po.status === "rejected") {
      // Rejection queue task - back to salesperson
      tasks.push({
        taskId: taskId++,
        queueType: "rejection",
        entityType: "purchase_order",
        entityId: po.purchaseOrderId,
        entityNumber: po.poNumber,
        assignedTo: po.requestedBy,
        assignedToName: po.requestedByName || "Salesperson",
        plantId: po.plantId,
        plantCode: po.plantCode,
        title: `Fix Rejected PO ${po.poNumber}`,
        description: po.rejectionReason || "See rejection notes",
        priority: "high",
        status: "pending",
        createdDate: new Date().toISOString().split("T")[0],
        action: "rejected",
        actionNotes: po.rejectionReason,
        actionBy: 1,
        actionByName: "Clay",
        vendorName: po.vendorName,
        totalAmount: po.totalAmount,
        createdAt: po.createdAt,
        updatedAt: po.updatedAt,
      });
    }

    // Add some completed tasks for history
    if (po.status === "paid") {
      // Completed receiving task
      tasks.push({
        taskId: taskId++,
        queueType: "receiving",
        entityType: "purchase_order",
        entityId: po.purchaseOrderId,
        entityNumber: po.poNumber,
        assignedTo: 2,
        assignedToName: "Jason",
        plantId: po.plantId,
        plantCode: po.plantCode,
        title: `Receive PO ${po.poNumber}`,
        description: `${po.vendorName} - Received`,
        priority: "normal",
        status: "completed",
        createdDate: po.orderDate,
        completedDate: po.receivedDate,
        action: "received",
        actionBy: 2,
        actionByName: "Jason",
        vendorName: po.vendorName,
        totalAmount: po.totalAmount,
        createdAt: po.createdAt,
        updatedAt: po.updatedAt,
      });

      // Completed approval task
      tasks.push({
        taskId: taskId++,
        queueType: "approval",
        entityType: "purchase_order",
        entityId: po.purchaseOrderId,
        entityNumber: po.poNumber,
        assignedTo: 1,
        assignedToName: "Clay",
        plantId: po.plantId,
        plantCode: po.plantCode,
        title: `Approve PO ${po.poNumber}`,
        description: `${po.vendorName} - Approved`,
        priority: "normal",
        status: "completed",
        createdDate: po.receivedDate || po.orderDate,
        completedDate: po.approvedDate,
        action: "approved",
        actionBy: 1,
        actionByName: "Clay",
        vendorName: po.vendorName,
        totalAmount: po.totalAmount,
        createdAt: po.createdAt,
        updatedAt: po.updatedAt,
      });

      // Completed payment task
      tasks.push({
        taskId: taskId++,
        queueType: "payment",
        entityType: "purchase_order",
        entityId: po.purchaseOrderId,
        entityNumber: po.poNumber,
        assignedTo: 3,
        assignedToName: "Allie",
        plantId: po.plantId,
        plantCode: po.plantCode,
        title: `Pay PO ${po.poNumber}`,
        description: `${po.vendorName} - Paid`,
        priority: "normal",
        status: "completed",
        createdDate: po.approvedDate || po.orderDate,
        completedDate: po.paidDate,
        action: "paid",
        actionBy: 3,
        actionByName: "Allie",
        vendorName: po.vendorName,
        totalAmount: po.totalAmount,
        createdAt: po.createdAt,
        updatedAt: po.updatedAt,
      });
    }
  });

  return tasks;
};

/**
 * Mock approval queue tasks data
 */
export const mockApprovalQueueTasks: ApprovalQueueTask[] = generateMockTasks();

/**
 * Create mock approval queue store
 */
export const createMockApprovalQueueStore = () =>
  new MockServiceStore<ApprovalQueueTask>({
    mockData: mockApprovalQueueTasks,
    idField: "taskId",
    simulateDelay: 200,
  });

export default mockApprovalQueueTasks;
